import { forwardRef, useMemo, useState, useCallback } from "react";
import styles from "./GridResultCard.module.scss";
import { useIntl } from "react-intl";
import { getReaderNavURL } from "../../../bookshelfLinks.ts";
import EmptyAssetCard from "../../../../common/features/asset/EmptyAssetCard.tsx";
import WhiteAssetCardContainer from "../../../../common/features/asset/WhiteAssetCardContainer.tsx";
import { SearchCardProps } from "./types.ts";
import ResultCountButton from "./ResultCountButton.tsx";
import BookDetails from "./BookDetails.tsx";
import AssetTopRow from "../../../../common/features/asset/AssetTopRow.tsx";
import DefaultCoverBadge from "../../../../common/features/asset/DefaultCoverBadge.tsx";
import useFavorites from "../../actions/favorites/useFavorites.tsx";
import useAccessibilityClaimsLink from "../../common/accessibility-claims/useAccessibilityClaimsLink.tsx";
import { PanelType } from "../../common/asset-panel/types.ts";
import AssetPanel from "../../common/asset-panel/AssetPanel.tsx";

interface PopoverLink {
  label: string;
  onClick?: () => void;
  href?: string;
}

function GridResultCard(
  {
    searchResult,
    openPanel,
    dynamicStyles,
    libraryReturn,
    section,
  }: SearchCardProps,
  ref: React.ForwardedRef<HTMLElement>
) {
  const intl = useIntl();
  const readerUrl = getReaderNavURL(searchResult.asset.manageAsset.vbid);
  const asset = searchResult.asset;
  const { favoritesMenuLink } = useFavorites(
    searchResult.asset.manageAsset,
    searchResult.asset.favorite
  );

  const [openPanelInfo, setOpenPanelInfo] = useState<{
    panel: string;
    time: number;
  }>();

  const setOpenAccessibilityPanel = useCallback(() => {
    setOpenPanelInfo({ panel: PanelType.Accessibility, time: Date.now() });
  }, []);

  const a11yClaimsMenuLink = useAccessibilityClaimsLink(
    setOpenAccessibilityPanel
  );

  const popoverLinks: PopoverLink[] = useMemo(() => {
    return [
      {
        label: intl.formatMessage({ defaultMessage: "Open Book" }),
        href: readerUrl,
      },
      {
        label: intl.formatMessage({ defaultMessage: "View Content Results" }),
        onClick: () => openPanel(asset, section),
        href: "",
      },
      a11yClaimsMenuLink,
      favoritesMenuLink,
    ];
  }, [
    intl,
    readerUrl,
    favoritesMenuLink,
    openPanel,
    asset,
    section,
    a11yClaimsMenuLink,
  ]);

  return (
    <WhiteAssetCardContainer tagName="li" dynamicStyles={dynamicStyles}>
      <EmptyAssetCard
        vbid={asset.manageAsset.vbid}
        type="horizontal"
        tagName="div"
        imageChildren={<DefaultCoverBadge license={asset.license} />}
        primaryAction={{ href: popoverLinks[0].href }}
        ref={ref}
      >
        <AssetTopRow
          asset={asset.manageAsset}
          license={asset.license}
          links={popoverLinks}
          compact={true}
        />
        <BookDetails
          asset={asset.manageAsset}
          className={styles.bookDetails}
          assetLink={popoverLinks[0].href}
          showEdition
        />
        <div className={styles.stickyBottom}>
          <ResultCountButton
            onClick={() => openPanel(searchResult.asset, section)}
            count={searchResult.count}
            title={asset.manageAsset.title}
            libraryReturn={libraryReturn}
          />
        </div>
        {openPanelInfo?.panel === PanelType.Accessibility && (
          <AssetPanel
            asset={asset.manageAsset}
            onRequestClose={() => setOpenPanelInfo(undefined)}
            key={openPanelInfo.time}
            startTab={PanelType.Accessibility}
          />
        )}
      </EmptyAssetCard>
    </WhiteAssetCardContainer>
  );
}

const ResultCard = forwardRef<HTMLElement, SearchCardProps>(GridResultCard);

export default ResultCard;
